import uniq from 'lodash/uniq';
import PropTypes from 'prop-types';
import React, { useCallback, useContext, useMemo, useState } from 'react';

import { useApi } from './ApiContext';

const UserContext = React.createContext(null);

export const useUserContext = () => useContext(UserContext);

export function useUser() {
    const { user = null } = useUserContext() || {};
    return user;
}

export function useSetUser() {
    const { setUser = null } = useUserContext() || {};
    return setUser;
}

export function useCleartUser() {
    const { clearUser = null } = useUserContext() || {};
    return clearUser;
}

export function useFavorites() {
    const { favorites, setFavorites, onChangeFavorite, hasFavorite } = useUserContext() || {};
    return { favorites, setFavorites, onChangeFavorite, hasFavorite };
}

const propTypes = {
    children: PropTypes.node.isRequired,
    user: PropTypes.shape({}),
    favorites: PropTypes.arrayOf(PropTypes.string),
};

export const UserProvider = ({
    user: initialUser = null,
    favorites: initialFavorites = null,
    children = null,
}) => {
    const { favorites: favoritesApi } = useApi();
    const [user, setUserState] = useState(initialUser);
    const [favorites, setFavoritesState] = useState(initialFavorites || []);

    const setUser = useCallback((data) => setUserState(data), [setUserState]);
    const setFavorites = useCallback((data) => setFavoritesState(data), [setFavoritesState]);
    const addFavorite = useCallback(
        (id) => uniq(setFavoritesState([...favorites, id])),
        [favorites, setFavoritesState],
    );
    const removeFavorite = useCallback(
        (id) => setFavoritesState(favorites.filter((favorite) => favorite !== id)),
        [favorites, setFavoritesState],
    );
    const hasFavorite = useCallback((id) => favorites.includes(id), [favorites]);

    const onChangeFavorite = useCallback(
        (id) => {
            const isFavorite = hasFavorite(id);
            if (isFavorite) {
                removeFavorite(id);
                favoritesApi.destroy({ id });
            } else {
                addFavorite(id);
                favoritesApi.store({ id });
            }
        },
        [hasFavorite, addFavorite, removeFavorite],
    );

    const clearUser = useCallback(() => setUserState(null), [setUserState]);

    const value = useMemo(
        () => ({
            user,
            setUser,
            clearUser,
            favorites,
            setFavorites,
            addFavorite,
            removeFavorite,
            hasFavorite,
            onChangeFavorite,
        }),
        [
            user,
            setUser,
            clearUser,
            favorites,
            setFavorites,
            addFavorite,
            removeFavorite,
            hasFavorite,
            onChangeFavorite,
        ],
    );

    return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

UserProvider.propTypes = propTypes;

export default UserContext;
